// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Checkbox } from 'informed';

import {
  addAdditionalImmunization,
  deleteAdditionalImmunization,
  updateAdditionalImmunization,
} from '../../../actions/immunizationActions';

import DateText from '../../DateText.jsx';
import Table from '../../Table.jsx';
import { Error } from '../../Typography.jsx';
import Typeahead from '../../Typeahead.jsx';

type Props = {
  activePatientId: string,
  dob: string,
  additionalImmunizations: Object[],
  addAdditionalImmunization: Function,
  deleteAdditionalImmunization: Function,
  updateAdditionalImmunization: Function,
}

class AdditionalImmunizationsComponent extends Component<Props> {
  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.dateAdministered = moment(formValues.dateAdministered, 'DD-MM-YYYY').toISOString();
    formValues.dateDue = moment(formValues.dateDue, 'DD-MM-YYYY').toISOString();
  }

  handleDeleteRow = (id: string) => {
    this.props.deleteAdditionalImmunization(this.props.activePatientId, id);
  }

  handleSaveRow = (id: string, formData: Object) => {
    this.transformDates(formData.values);
    this.props.updateAdditionalImmunization(this.props.activePatientId, {
      immunizationId: id,
      ...formData.values,
    });
  }

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    this.props.addAdditionalImmunization(this.props.activePatientId, formData.values);
  }

  // overdue if no administered date and exists due date earlier than now
  isOverdue = item => (!item.dateAdministered
    && !item.overridden
    && item.dateDue
    && moment(item.dateDue) < moment())

  render() {
    const columns = [
      {
        Header: 'Age',
        id: 'age',
        editCell: item => <Typeahead field='age' initialValue={item.age} endpoint='age' />,
        width: 120,
      },
      {
        Header: 'Immunization',
        id: 'immunization',
        editCell: item => <Typeahead field='immunization' initialValue={item.immunization} endpoint='immunization' />,
      },
      {
        Header: 'Lot #',
        id: 'lotNum',
        editCell: item => <Typeahead field='lotNum' initialValue={item.lotNum} endpoint='lotNum' />,
      },
      {
        Header: 'Date Due',
        id: 'dateDue',
        cell: item => (item.dateDue ? moment(item.dateDue).format() : '--'),
        editCell: item => <DateText field='dateDue' item={item} initEmpty />,
        width: 120,
      },
      {
        Header: 'Date Administered',
        id: 'dateAdministered',
        cell: (item) => {
          if (item.overridden) {
            return '(overridden)';
          }
          const overdue = this.isOverdue(item);
          return overdue ? <Error>-- (overdue)</Error>
            : item.dateAdministered ? moment(item.dateAdministered).format() : '--';
        },
        editCell: item => <div>
          <DateText field='dateAdministered' item={item} />
          <br />
          <Checkbox field='overridden' initialValue={item.overridden} />Override
        </div>,
        width: 130,
      },
    ];

    let { additionalImmunizations } = this.props;
    additionalImmunizations = additionalImmunizations.sort((a, b) => {
      // overdues go first
      const aOverdue = this.isOverdue(a);
      const bOverdue = this.isOverdue(b);
      if (!aOverdue && bOverdue) {
        return 1;
      }
      if (!bOverdue && aOverdue) {
        return -1;
      }

      // otherwise based on date due or date administered if date due DNE
      let aDate = a.dateDue;
      if (!aDate && !a.overridden) {
        aDate = a.dateAdministered;
      }
      let bDate = b.dateDue;
      if (!bDate && !b.overridden) {
        bDate = b.dateAdministered;
      }
      if (!aDate) {
        return 1;
      }
      if (!bDate) {
        return -1;
      }
      if (aDate < bDate) {
        return 1;
      }
      if (bDate < aDate) {
        return -1;
      }
      return 0;
    });

    return <Table
      columns={columns}
      data={additionalImmunizations}
      handleDeleteRow={this.handleDeleteRow}
      handleSaveRow={this.handleSaveRow}
      handleSaveNewRow={this.handleSaveNewRow}
    />;
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
  dob: state.patient.data.generalInfo.dateArrived,
  additionalImmunizations: state.patient.data.additionalImmunizations,
});

const mapDispatchToProps = dispatch => ({
  addAdditionalImmunization: (patientId, additionalImmunization) => dispatch(
    addAdditionalImmunization(patientId, additionalImmunization),
  ),
  deleteAdditionalImmunization: (patientId, additionalImmunizationId) => dispatch(
    deleteAdditionalImmunization(patientId, additionalImmunizationId),
  ),
  updateAdditionalImmunization: (patientId, additionalImmunization) => dispatch(
    updateAdditionalImmunization(patientId, additionalImmunization),
  ),
});

const AdditionalImmunizations = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AdditionalImmunizationsComponent);

export default AdditionalImmunizations;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/immunization/AdditionalImmunizations.jsx